import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';
import { formatInTimeZone } from 'date-fns-tz';
import type TaskEventModel from 'uplisting-frontend/models/task-event';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { TaskAssignmentState } from 'uplisting-frontend/models/task-assignment';
import { propertyDisplayName } from 'uplisting-frontend/utils/property';
import { channelIcon } from 'uplisting-frontend/utils/channel-icon';

export enum TaskKind {
  custom = 'custom',
  changeover = 'changeover',
  maintenance = 'maintenance',
  cleaning = 'cleaning',
  damageReport = 'damage_report',
  dispute = 'dispute',
  compliance = 'compliance',
}

const RAISED_BY_THE_BACKEND: TaskKind[] = [
  TaskKind.changeover,
  TaskKind.dispute,
  TaskKind.compliance,
];

export const CREATABLE_TASK_KINDS: TaskKind[] = Object.values(TaskKind).filter(
  (kind) => !RAISED_BY_THE_BACKEND.includes(kind),
);

export const DAMAGE_REPORTABLE_TASK_KINDS: TaskKind[] = [
  TaskKind.changeover,
  TaskKind.cleaning,
  TaskKind.maintenance,
];

// Due on a day rather than at a time, because the date comes from the source (a compliance
// record's expiry) rather than from anyone booking the work in.
const SYSTEM_DATED_KINDS: TaskKind[] = [TaskKind.compliance];

export enum TaskProgressState {
  pending = 'pending',
  accepted = 'accepted',
  started = 'started',
  finished = 'finished',
}

export enum TaskStatus {
  pendingReview = 'pending_review',
  open = 'open',
  inProgress = 'in_progress',
  blocked = 'blocked',
  done = 'done',
  cancelled = 'cancelled',
}

export enum TaskPriority {
  low = 'low',
  normal = 'normal',
  high = 'high',
  urgent = 'urgent',
}

export enum TaskAssignmentSummary {
  unassigned = 'unassigned',
  awaitingAcceptance = 'awaiting_acceptance',
  accepted = 'accepted',
  declined = 'declined',
}

// Mirrors `Task::TRANSITIONS` in the API. Kept here so the UI never offers a move the backend
// will refuse — a disabled control beats a 422 the person cannot act on.
export const TASK_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  [TaskStatus.pendingReview]: [TaskStatus.open, TaskStatus.cancelled],
  [TaskStatus.open]: [
    TaskStatus.inProgress,
    TaskStatus.blocked,
    TaskStatus.cancelled,
  ],
  [TaskStatus.inProgress]: [
    TaskStatus.open,
    TaskStatus.blocked,
    TaskStatus.done,
    TaskStatus.cancelled,
  ],
  [TaskStatus.blocked]: [
    TaskStatus.open,
    TaskStatus.inProgress,
    TaskStatus.cancelled,
  ],
  [TaskStatus.done]: [TaskStatus.inProgress],
  [TaskStatus.cancelled]: [TaskStatus.open],
};

// The single move the primary button offers. Everything else the backend allows from here is
// still reachable, but behind a menu, so the common step forward stays one click.
const FORWARD_STATUS: Record<TaskStatus, TaskStatus> = {
  [TaskStatus.pendingReview]: TaskStatus.open,
  [TaskStatus.open]: TaskStatus.inProgress,
  [TaskStatus.inProgress]: TaskStatus.done,
  [TaskStatus.blocked]: TaskStatus.inProgress,
  [TaskStatus.done]: TaskStatus.inProgress,
  [TaskStatus.cancelled]: TaskStatus.open,
};

const HIGH_PRIORITIES: string[] = [TaskPriority.high, TaskPriority.urgent];

export interface ChecklistSection {
  section: string | undefined;
  items: ModelRegistry['task-checklist-item'][];
  completedCount: number;
  requiredCount: number;
}

export default class TaskModel extends Model {
  @attr('array') upNext!: { user_id: number; name: string | null }[];
  @attr('number') assigneeUserId!: number | null;

  @attr('string') kind!: TaskKind;
  @attr('string') title!: string;
  @attr('string') description?: string;
  @attr('string') status!: TaskStatus;
  @attr('string') assignmentState!: TaskAssignmentSummary;
  @attr('string') priority?: TaskPriority;
  @attr('string') dueAt?: string;
  @attr('string') completedAt?: string;
  @attr('boolean') overdue!: boolean;
  @attr('boolean') unreadTeamMessages!: boolean;
  @attr('string') bookingChannel?: string;
  @attr('string') sourceType!: string;
  @attr('string') sourceId?: string;
  @attr('number') checklistItemsCount!: number;
  @attr('number') checklistItemsCompletedCount!: number;

  @hasMany('task-assignment', { inverse: null, async: false })
  taskAssignments!: ModelRegistry['task-assignment'][];

  @hasMany('task-checklist-item', { inverse: null, async: false })
  taskChecklistItems!: ModelRegistry['task-checklist-item'][];

  @hasMany('task-attachment', { inverse: null, async: false })
  taskAttachments!: ModelRegistry['task-attachment'][];

  @hasMany('task-event', { inverse: null, async: false })
  taskEvents!: TaskEventModel[];

  @belongsTo('property', { inverse: null, async: false })
  property?: ModelRegistry['property'];

  @belongsTo('multi-unit', { inverse: null, async: false })
  multiUnit?: ModelRegistry['multi-unit'];

  @belongsTo('dispute', { inverse: null, async: false })
  dispute?: ModelRegistry['dispute'];

  @belongsTo('compliance-document', { inverse: null, async: false })
  complianceDocument?: ModelRegistry['compliance-document'];

  @belongsTo('booking', { inverse: null, async: false })
  booking?: ModelRegistry['booking'];

  @belongsTo('booking', { inverse: null, async: false })
  nextBooking?: ModelRegistry['booking'];

  @cached
  get propertyAddress(): string | undefined {
    const { property } = this;

    if (!property) {
      return undefined;
    }

    const address = property.belongsTo('address').value() as
      | ModelRegistry['address']
      | null;

    return address?.fullAddress;
  }

  @cached
  get propertyLabel(): string | undefined {
    return propertyDisplayName(this.property, this.multiUnit) || undefined;
  }

  @cached
  get assignee(): ModelRegistry['task-assignment'] | undefined {
    const ordered = this.taskAssignments
      .slice()
      .sort((a, b) => a.position - b.position || Number(a.id) - Number(b.id));

    return (
      ordered.find((assignment) => assignment.isAccepted) ??
      ordered.find((assignment) => assignment.isOffered)
    );
  }

  @cached
  get assigneeName(): string | undefined {
    return this.assignee?.assigneeName;
  }

  @cached
  get isUnassigned(): boolean {
    return this.assignmentState === TaskAssignmentSummary.unassigned;
  }

  @cached
  get isDone(): boolean {
    return this.status === TaskStatus.done;
  }

  @cached
  get isCancelled(): boolean {
    return this.status === TaskStatus.cancelled;
  }

  @cached
  get isHighPriority(): boolean {
    return HIGH_PRIORITIES.includes(this.priority as string);
  }

  @cached
  get channelIcon(): string | undefined {
    return channelIcon(this.bookingChannel);
  }

  @cached
  get isSystemDated(): boolean {
    return SYSTEM_DATED_KINDS.includes(this.kind);
  }

  @cached
  get allowedStatuses(): TaskStatus[] {
    return TASK_TRANSITIONS[this.status] ?? [];
  }

  @cached
  get forwardStatus(): TaskStatus | undefined {
    return FORWARD_STATUS[this.status];
  }

  @cached
  get otherStatuses(): TaskStatus[] {
    return this.allowedStatuses.filter(
      (status) => status !== this.forwardStatus,
    );
  }

  @cached
  get hasChecklist(): boolean {
    return this.checklistItemsCount > 0;
  }

  @cached
  get requiredIncompleteCount(): number {
    return this.sortedChecklistItems.filter(
      (item) => item.required && !item.isCompleted,
    ).length;
  }

  @cached
  get photographedChecklistItemIds(): Set<string> {
    const ids = new Set<string>();

    for (const attachment of this.taskAttachments) {
      if (attachment.taskChecklistItemId) {
        ids.add(attachment.taskChecklistItemId);
      }
    }

    return ids;
  }

  @cached
  get missingPhotoCount(): number {
    const photographed = this.photographedChecklistItemIds;

    return this.sortedChecklistItems.filter(
      (item) => item.requiresPhoto && !photographed.has(item.id),
    ).length;
  }

  @cached
  get sortedChecklistItems(): ModelRegistry['task-checklist-item'][] {
    return this.taskChecklistItems
      .slice()
      .sort((a, b) => a.position - b.position || Number(a.id) - Number(b.id));
  }

  @cached
  get sortedTaskAttachments(): ModelRegistry['task-attachment'][] {
    return this.taskAttachments
      .slice()
      .sort((a, b) => Number(a.id) - Number(b.id));
  }

  @cached
  get checklistSections(): ChecklistSection[] {
    const groups = new Map<
      string | undefined,
      ModelRegistry['task-checklist-item'][]
    >();

    for (const item of this.sortedChecklistItems) {
      const key = item.section || undefined;
      const list = groups.get(key) ?? [];

      list.push(item);
      groups.set(key, list);
    }

    return [...groups.entries()].map(([section, items]) => ({
      section,
      items,
      completedCount: items.filter((i) => i.isCompleted).length,
      requiredCount: items.filter((i) => i.required).length,
    }));
  }

  @cached
  get progressState(): TaskProgressState {
    if (this.status === TaskStatus.done) {
      return TaskProgressState.finished;
    }

    if (this.status === TaskStatus.inProgress) {
      return TaskProgressState.started;
    }

    if (this.assignmentState === TaskAssignmentSummary.accepted) {
      return TaskProgressState.accepted;
    }

    return TaskProgressState.pending;
  }

  @cached
  get dueAtAsDate(): Date | undefined {
    return this.dueAt ? parseISO(this.dueAt) : undefined;
  }

  @cached
  get displayTimeZone(): string | undefined {
    const listingZoned =
      this.kind === TaskKind.changeover || this.isSystemDated;

    return listingZoned ? this.property?.timeZone : undefined;
  }

  // The day the task falls on. A system-dated task is due at the end of a day where its
  // listing is, so that day is read in the listing's zone rather than the viewer's.
  @cached
  get dueDay(): Date | undefined {
    const { dueAtAsDate } = this;

    if (!dueAtAsDate) {
      return undefined;
    }

    const { displayTimeZone: timeZone } = this;

    return timeZone
      ? parseISO(formatInTimeZone(dueAtAsDate, timeZone, 'yyyy-MM-dd'))
      : startOfDay(dueAtAsDate);
  }

  // An overdue row answers "how late", not "what time" — the clock time stopped being the point.
  @cached
  get daysLate(): number {
    const { dueDay } = this;

    if (!this.overdue || !dueDay) {
      return 0;
    }

    return differenceInCalendarDays(new Date(), dueDay);
  }

  @cached
  get durationMinutes(): number | undefined {
    return this.taskAssignments.find(
      (assignment) => assignment.state === TaskAssignmentState.accepted,
    )?.durationMinutes;
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    task: TaskModel;
  }
}
